import React from 'react'
import styled from 'styled-components'
import { PictureAsPdfOutlined } from '@mui/icons-material'

import { Panel } from 'components/Panel'
import Spinner from 'components/Spinner'
import Table from 'components/Table'

import { shortDateTime } from 'helpers/formatters'
import Apptentive from 'helpers/apptentive'
import GA4 from 'helpers/ga4'

import { usePremiumFeaturesContext } from 'hooks/PremiumFeaturesContext'
import { useStoreContext } from 'hooks/StoreContext'

import { color } from 'styles/colors'
import { screenSize } from 'styles/shared'

const SystemReportsPanel = () => {
  const { systemReports, areSystemReportsLoaded } = useStoreContext()
  const { hasAccess, showUpgrade } = usePremiumFeaturesContext()

  const onClickReport = () => {
    GA4.sendEvent({
      category: 'Customer Detail Page',
      action: 'Click PDF Report',
      label: 'Report Type: System'
    })

    Apptentive.sendEvent('system_pdf_report_viewed')
  }

  const SortByTimeStamp = (reports) => {
    return reports?.sort((a, b) => {
      const dateA = new Date(a.createdAt).valueOf()
      const dateB = new Date(b.createdAt).valueOf()
      return dateB - dateA
    }) || []
  }

  return (
    <S.Panel id="link-system-reports" scrollToSection>
      <Panel.Header>
        <Panel.Title>System Reports</Panel.Title>
      </Panel.Header>

      {!areSystemReportsLoaded && <Spinner variant="page" />}

      <>
        {areSystemReportsLoaded && (
          <Table
            items={(hasAccess('system_reports_history') && systemReports) || []}
            noDataMessage={
              {
                title: showUpgrade('system_reports_history') ? 'Upgrade to Premium' : 'No Saved Reports',
                description: showUpgrade('system_reports_history') ? 'Upgrade to Diagnostics Premium to view saved system reports' : 'Reports will be displayed here if a system report is downloaded with the Save option selected.'
              }
            }
          >
            <Table.Header>
              <Table.HeaderCell title="Name" />
              <Table.HeaderCell title="Created" />
            </Table.Header>
            {SortByTimeStamp(systemReports).map((report) => (
              <Table.Row key={report.s3FileName}>
                <S.Cell>
                  <S.ReportFileLink
                    onClick={onClickReport}
                    href={report.url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`View report: ${report.s3FileName}`}
                  >
                    <S.PdfIcon sx={{ fontSize: 40 }} />

                    <div>{report.s3FileName}</div>
                  </S.ReportFileLink>
                </S.Cell>
                <S.Cell><S.ReportTimeStamp>{shortDateTime(report.createdAt)}</S.ReportTimeStamp></S.Cell>
              </Table.Row>
            ))}
          </Table>
        )}
      </>
    </S.Panel>
  )
}

export default SystemReportsPanel

const S = {
  Panel: styled(Panel)`
    scroll-margin-top: 60px;
    background-color: ${color('paneBackground')};
  `,
  ReportFileLink: styled.a`
    margin: 15px 0;
    display: flex;

    @media ${screenSize.small} {
      margin: 0;
    }
  `,
  ReportTimeStamp: styled.div`
    margin: 15px 0;
    display: flex;

    @media ${screenSize.small} {
      margin: 0 0 0 35px;
    }
  `,
  Cell: styled(Table.Cell)`
    padding-left: 15px;

    @media ${screenSize.small} {
      padding-left: 0;
    }
  `,
  PdfIcon: styled(PictureAsPdfOutlined)`
    margin-right: 10px;
    font-size: 24px;
  `
}
